import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ShieldQuestion, Loader2, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * The member's side of View-As: what they were asked, and their answer.
 *
 * Shows nothing at all when nobody has asked — which is almost always. When access is open,
 * the same panel is how they take it back, so permission is never a one-way door.
 */
export const ViewAsConsentPanel = () => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/view-as/my-requests`, { headers });
      setRequests(res.data.requests || []);
    } catch { setRequests([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const answer = async (id, action) => {
    setBusy(id);
    try {
      await axios.post(`${API}/view-as/${id}/${action}`, {}, { headers });
      toast.success(action === 'approve'
        ? 'Thanks — they can now see your account to help'
        : 'Done. Your account is private again.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save that");
    } finally { setBusy(null); }
  };

  if (!requests.length) return null;

  return (
    <div className="space-y-3" data-testid="view-as-consent-panel">
      {requests.map(r => (
        <div
          key={r.id}
          className="rounded-xl border p-4"
          style={{
            borderColor: r.status === 'approved' ? 'rgba(110,231,199,0.4)' : 'rgba(225,150,60,0.45)',
            background: r.status === 'approved' ? 'rgba(110,231,199,0.07)' : 'rgba(225,150,60,0.08)',
          }}
          data-testid={`view-as-request-${r.id}`}
        >
          <div className="flex items-start gap-3">
            <ShieldQuestion className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#e1963c' }} />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">
                {r.status === 'approved'
                  ? `${r.admin_name} can currently see your account`
                  : `${r.admin_name} is asking to see your account`}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                To help with: <em>{r.reason}</em>
              </p>
              <p className="text-xs text-gray-500 mt-2">
                They can never see your password, move your money or change your bank details, and
                you can withdraw this whenever you like.
              </p>
              <div className="flex gap-2 mt-3">
                {r.status === 'pending' ? (
                  <>
                    <Button size="sm" onClick={() => answer(r.id, 'approve')}
                            disabled={busy === r.id} className="gap-1"
                            data-testid={`view-as-approve-${r.id}`}>
                      {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" />
                                     : <Check className="w-3 h-3" />}
                      Allow
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => answer(r.id, 'decline')}
                            disabled={busy === r.id} className="gap-1"
                            data-testid={`view-as-decline-${r.id}`}>
                      <X className="w-3 h-3" /> No thanks
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => answer(r.id, 'decline')}
                          disabled={busy === r.id} className="gap-1"
                          data-testid={`view-as-revoke-${r.id}`}>
                    {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" />
                                   : <X className="w-3 h-3" />}
                    Withdraw access
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ViewAsConsentPanel;
